import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat } from '@paige/format';
import { OpsCompanyName } from '../ops/OpsCompanyName';
import type { PendingAction } from '../problems/ProblemActionDialog';
import { ProblemRow } from '../problems/ProblemRow';
import type {
  ProblemFile,
  ProblemFilesPage,
  ProblemGroup,
  Problems,
} from '../problems/problem-types';
import type { FlowLinks } from './FlowDetail';
import type { FlowIncident, FlowIncidentCompaniesPage } from './flow-types';
import { unitWord } from './flow-words';
import { incidentHeadline } from './incident-words';
import type { ViewerAsk } from './viewer-types';

/** What the panel reads for an incident's "who": Problems' rows for a parked cause, the companies for the rest. */
export interface IncidentLoaders {
  /** Problems narrowed to one reason (`GET /admin/problems?reason=`), a page of rows. */
  problems: (reason: string, limit: number) => Promise<Problems>;
  files: (group: ProblemGroup, offset: number) => Promise<ProblemFilesPage>;
  /** One cause's companies, largest first (`GET /admin/flow/incidents/companies`). */
  companies: (incident: FlowIncident, offset: number) => Promise<FlowIncidentCompaniesPage>;
}

interface Read<T> {
  key: string;
  value: T | null;
  failed: string | null;
}

/** Reads whenever the key changes; the last answer stays up meanwhile. */
function useRead<T>(key: string, read: () => Promise<T>): Read<T> {
  const [state, setState] = useState<Read<T>>({ key: '', value: null, failed: null });
  useEffect(() => {
    let current = true;
    read().then(
      (value) => current && setState({ key, value, failed: null }),
      (e: unknown) =>
        current &&
        setState({ key, value: null, failed: e instanceof Error ? e.message : String(e) }),
    );
    return () => {
      current = false;
    };
    // The key carries everything the read depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

const ROWS = 5;

/** A parked cause's Problems rows, read on open and when its counts change; the card is told the companies. */
function useParked(
  props: {
    incident: FlowIncident;
    loaders: IncidentLoaders;
    stamp: string;
    onGroups: (g: ProblemGroup[]) => void;
  },
  limit: number,
): Read<Problems> {
  const { incident, loaders, stamp, onGroups } = props;
  const read = useRead(`${incident.id}|${limit}|${stamp}`, () =>
    loaders.problems(incident.reason ?? '', limit),
  );
  useEffect(() => {
    if (read.value) onGroups(read.value.groups);
  }, [read.value, onGroups]);
  return read;
}

/** A file line's "See it": the support viewer on its batch, through the grant, the incident given as the reason. */
const seeFileOf = (onSee: ((ask: ViewerAsk) => void) | undefined, incident: FlowIncident) =>
  onSee
    ? (group: ProblemGroup, file: ProblemFile) =>
        onSee({
          company: { id: group.company.id, name: group.company.name },
          batchId: file.batchId ?? '',
          fileName: file.name,
          reason: `Stages and queues: ${incidentHeadline(incident)}`,
        })
    : undefined;

/** While the companies are read, or why they could not be. */
function Reading({ failed }: { failed: string | null }): ReactNode {
  return (
    <p className="fl-inc__note">
      {failed ? `Could not read the companies: ${failed}` : 'Reading the companies'}
    </p>
  );
}

/** A parked cause: the Problems rows of its companies, each opening to its files, actions and stories. */
interface WhoProps {
  incident: FlowIncident;
  loaders: IncidentLoaders;
  links: FlowLinks;
  stamp: string;
  onAsk: ((p: PendingAction) => void) | undefined;
  onGroups: (groups: ProblemGroup[]) => void;
  onSee?: ((ask: ViewerAsk) => void) | undefined;
}

function ParkedWho(props: WhoProps): ReactNode {
  const { loaders, links, stamp, onAsk, onSee, incident } = props;
  const [limit, setLimit] = useState(ROWS);
  const [open, setOpen] = useState<string | null>(null);
  const read = useParked(props, limit);
  if (!read.value) return <Reading failed={read.failed} />;
  const { groups, matching } = read.value;
  const row = (g: ProblemGroup) => (
    <ProblemRow
      key={g.id}
      group={g}
      links={links}
      open={open === g.id || groups.length === 1}
      onToggle={() => setOpen(open === g.id ? null : g.id)}
      loadFiles={loaders.files}
      stamp={stamp}
      onAsk={onAsk}
      onSeeFile={seeFileOf(onSee, incident)}
    />
  );
  return (
    <>
      <ul className="pb-rows fl-inc__rows" aria-label="Companies and files">
        {groups.map(row)}
      </ul>
      {matching > groups.length ? (
        <Button variant="secondary" size="sm" onClick={() => setLimit(limit + ROWS)}>
          Show more companies ({matching - groups.length} more)
        </Button>
      ) : null}
    </>
  );
}

/** Any other cause: its companies, largest first, each a link to the company. */
function CompaniesWho({
  incident,
  loaders,
  links,
  stamp,
}: {
  incident: FlowIncident;
  loaders: IncidentLoaders;
  links: FlowLinks;
  stamp: string;
}): ReactNode {
  const f = useFormat();
  const [offset, setOffset] = useState(0);
  const read = useRead(`${incident.id}|${offset}|${stamp}`, () =>
    loaders.companies(incident, offset),
  );
  if (!read.value) return <Reading failed={read.failed} />;
  const { companies, total } = read.value;
  return (
    <>
      <ul className="fl-inc__companies" aria-label="Companies">
        {companies.map((c) => (
          <li key={c.id} className="fl-inc__company">
            <OpsCompanyName company={c} href={links.company(c.slug)} Link={links.Link} />
            <span>
              {f.int(c.items)} {unitWord(incident.unit, c.items)}
              {c.jobName ? `, ${c.jobName}` : ''}, oldest {f.duration(c.oldestSeconds)}
            </span>
          </li>
        ))}
      </ul>
      {total > offset + companies.length ? (
        <Button variant="secondary" size="sm" onClick={() => setOffset(offset + companies.length)}>
          Next {Math.min(5, total - offset - companies.length)} of {f.int(total)}
        </Button>
      ) : null}
    </>
  );
}

/** Who is affected: companies (Test marked), jobs and files by name, and, for parked work, what staff can do. */
export function IncidentWho(props: WhoProps): ReactNode {
  return props.incident.class === 'parked' ? <ParkedWho {...props} /> : <CompaniesWho {...props} />;
}
